/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck - Material Tailwind component prop warnings
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@material-tailwind/react';
import AppRadio from '@/components/atoms/AppRadio';
import { Text, FieldLabel, Label, Caption } from '@/components/atoms/AppText';
import AppToggle from '@/components/atoms/AppToggle';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useToast } from '@/components/organism/Toast/ToastProvider';

import ConfigDropdown from '@/components/elements/ConfigDropdown';
import { Modal } from '@/components/elements/Modal';
import {
  clearCreatedProviderData,
  clearUpdatedProviderData,
  createProvider,
  updateProvider,
  getProviders,
  setShouldRefetchProviders,
  getProvider,
} from '@/store/slices/provider.slice';
import {
  PROVIDER_CONSTANT_PROPS,
  SERVER_OPTIONS,
  VDI_VERSION,
} from '@/constant/provider/provider';
import { tid } from '@/utils/testId';
import { PROVIDER_TEST_IDS as TID } from '@/constant/testIds/provider.testIds';
import {
  COMMON_MIN_MAX_VALIDATION_RULES,
  COMMON_VALIDATION_RULES,
} from '@/constant/global/global.constant';
import { USER_CONSTANT_PROPS } from '@/constant/users/user.constant';
import { getProviderList } from '@/store/selectors/providers.selector';
import { trimSpacesInPayload } from '@/utils/stringFormatting';
import _ from 'lodash';
import { isValidString } from '@/utils/generic';
import ConfigInput from '@/components/elements/ConfigInput';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { PageCard } from '@/components/organism/Cards/PageCard';
import {
  LuUser,
  LuBuilding2,
  LuHash,
  LuServer,
  LuRotateCcw,
  LuRefreshCw,
  LuFileText,
  LuSettings2,
} from 'react-icons/lu';
import { IoAddCircle, IoCheckmarkCircle } from 'react-icons/io5';
import { IoMdAddCircle } from 'react-icons/io';
import { useTheme } from '@/context/theme.context';
import { AppButton } from '@/components/atoms';
import { IconBadge } from '@/components/atoms/IconBadge';
import { APP_BG, APP_HEX } from '@/constant/theme/colors.constant';
import {
  isValidUrl,
  isValidProviderField,
} from './configProvider/urlValidator';
import { ICreateProviderFormInputs } from './provider.interface';
import { ProviderConfiguration, defaultConfigData } from './configProvider';

const CreateEditProvider: FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { id: providerId } = useParams<{ id: string }>();
  const location = useLocation();
  const locationState = location.state;
  const { provider: reduxProvider } = useSelector(getProviderList);
  // On refresh location.state is lost; fall back to Redux provider (fetched below)
  const providerData =
    locationState ??
    (providerId && reduxProvider?.providerId === providerId
      ? reduxProvider
      : null);
  const isEditMode = !!providerId;

  // After create → provider list; after edit → provider view page
  const goBack = useCallback(() => {
    if (providerId) {
      navigate(`/providers/${providerId}`, {
        replace: true,
        state: { providerId },
      });
    } else {
      navigate('/providers', { replace: true });
    }
  }, [navigate, providerId]);

  // Guard refs — prevent duplicate navigation when Redux dispatches re-trigger the success effect
  const hasNavigatedRef = useRef(false);
  const navTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [partnerId, setPartnerId] = useState(providerData?.partnerId || '');
  // Confirmation modal state
  const [isConfirm, setIsConfirm] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<any>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [name, setName] = useState(providerData?.name || '');
  const [server, setServer] = useState(providerData?.server || '');
  const [profile, setProfile] = useState(providerData?.profile || '');
  const [selectedServerObj, setSelectedServerObj] = useState<any>(null);
  const [serverTouched, setServerTouched] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [updateInitiated, setUpdateInitiated] = useState(false);
  const [vdiVersion, setVdiVersion] = useState(
    providerData?.vdiVersion || VDI_VERSION.v2,
  );
  const [syncVdi, setSyncVdi] = useState(providerData?.syncVdi ?? true);
  // Tracks the persisted value — toggle is locked once enabled (one-way: false→true only)
  const originalSyncVdiRef = useRef<boolean>(providerData?.syncVdi ?? true);

  // Config UI state — always merge with defaultConfigData so VDI_SETTINGS (and other keys) are never undefined
  const [configData, setConfigData] = useState(() => {
    const saved = providerData?.configs;
    if (!saved) return defaultConfigData;
    try {
      const parsed = typeof saved === 'string' ? JSON.parse(saved) : saved;
      return {
        ...defaultConfigData,
        ...parsed,
        VDI_SETTINGS: parsed?.VDI_SETTINGS ?? defaultConfigData.VDI_SETTINGS,
      };
    } catch {
      return defaultConfigData;
    }
  });
  const {
    createdProvider,
    provider: updatedProvider,
    error,
    updateSuccess,
  } = useSelector(getProviderList);

  // On page refresh location.state is wiped — fetch provider from API
  useEffect(() => {
    if (providerId && !locationState) {
      dispatch(getProvider(providerId));
    }
  }, [providerId]);

  // Once fetched provider arrives, hydrate local state
  useEffect(() => {
    if (
      !providerId ||
      locationState ||
      !reduxProvider ||
      reduxProvider.providerId !== providerId
    )
      return;
    setPartnerId(reduxProvider.partnerId || '');
    setName(reduxProvider.name || '');
    setServer(reduxProvider.server || '');
    setProfile(reduxProvider.profile || '');
    setSelectedServerObj(
      reduxProvider.server
        ? (SERVER_OPTIONS.find((opt) => opt.value === reduxProvider.server) ??
          null)
        : null,
    );
    setVdiVersion(reduxProvider.vdiVersion || VDI_VERSION.v2);
    setSyncVdi(reduxProvider.syncVdi ?? true);
    originalSyncVdiRef.current = reduxProvider.syncVdi ?? true;
    const saved = reduxProvider.configs;
    if (saved) {
      try {
        const parsed = typeof saved === 'string' ? JSON.parse(saved) : saved;
        setConfigData({
          ...defaultConfigData,
          ...parsed,
          VDI_SETTINGS: parsed?.VDI_SETTINGS ?? defaultConfigData.VDI_SETTINGS,
        });
      } catch {
        /* keep default */
      }
    }
    reset({
      partnerId: reduxProvider.partnerId || '',
      name: reduxProvider.name || '',
      server: reduxProvider.server || '',
      profile: reduxProvider.profile || '',
    });
    setValue('server', reduxProvider.server || '');
  }, [reduxProvider]);
  const [createdRecord, setCreatedRecord] = useState({
    name: '',
    partnerId: '',
  });

  const toast = useToast();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    reset,
    trigger,
    setValue,
    clearErrors,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<ICreateProviderFormInputs>({
    mode: 'onChange',
    defaultValues: {
      partnerId: providerData?.partnerId || '',
      name: providerData?.name || '',
      server: providerData?.server || '',
      profile: providerData?.profile || '',
    },
  });

  // Register form fields with validation
  React.useEffect(() => {
    register('server', {
      required: PROVIDER_CONSTANT_PROPS.VALIDATION.VMS_SERVER_REQUIRED,
      validate: (value) => {
        if (
          !value ||
          value === '' ||
          value ===
          PROVIDER_CONSTANT_PROPS.VALIDATION.SERVER_SELECT_PLACEHOLDER_ALT ||
          value === PROVIDER_CONSTANT_PROPS.VALIDATION.SERVER_SELECT_PLACEHOLDER
        ) {
          return PROVIDER_CONSTANT_PROPS.VALIDATION.VMS_SERVER_REQUIRED;
        }
        return true;
      },
    });
    register('profile', {
      required: PROVIDER_CONSTANT_PROPS.VALIDATION.PROFILE_NAME_REQUIRED,
      validate: isValidProviderField,
    });
    register('name', {
      required: PROVIDER_CONSTANT_PROPS.VALIDATION.PROVIDER_NAME_REQUIRED,
      validate: isValidProviderField,
    });
    register('partnerId', {
      validate: (v) => (!v ? true : isValidProviderField(v)),
    });
    register('configs');
  }, [register]);

  // Set initial server value
  React.useEffect(() => {
    if (isEditMode && providerData?.server) {
      setValue('server', providerData.server);
      setValue('name', providerData.name);
      setValue('profile', providerData.profile);
      if (providerData.partnerId) {
        setValue('partnerId', providerData.partnerId);
      }
    } else {
      setValue('server', '');
    }
  }, [setValue, isEditMode, providerData]);

  // Check if all mandatory fields are valid
  const isServerValid =
    isValidString(server) &&
    server !== PROVIDER_CONSTANT_PROPS.VALIDATION.SERVER_SELECT_PLACEHOLDER;
  const isProfileValid = isValidString(profile);
  const isNameValid = isValidString(name);

  // Validate config: each enabled service must have at least 1 VDI Type selected
  const configErrors = React.useMemo(() => {
    const errs: Record<string, string> = {};
    if (!configData.VDI_SETTINGS) return errs;
    const { MMS, VMS, CLIENTS, ALERTS, SMART_COOLERS } =
      configData.VDI_SETTINGS;

    // VDI Types validation
    if (MMS.ENABLED && (!MMS.VDI_TYPES || MMS.VDI_TYPES.length === 0)) {
      errs.MMS = 'At least 1 VDI Type is required';
    }
    if (VMS.ENABLED && (!VMS.VDI_TYPES || VMS.VDI_TYPES.length === 0)) {
      errs.VMS = 'At least 1 VDI Type is required';
    }
    if (
      CLIENTS.ENABLED &&
      (!CLIENTS.VDI_TYPES || CLIENTS.VDI_TYPES.length === 0)
    ) {
      errs.CLIENTS = 'At least 1 VDI Type is required';
    }

    // Host URL mandatory + format validation
    const validateHost = (
      auth: { HOST: string; AUTH_TYPE: string },
      enabled: boolean,
      label: string,
    ): string | undefined => {
      if (!enabled || !auth.AUTH_TYPE || auth.AUTH_TYPE === 'none')
        return undefined;
      if (!auth.HOST) return `Host URL is required for ${label}`;
      if (!isValidUrl(auth.HOST)) return `Must be a valid URL for ${label}`;
      return undefined;
    };

    const mmsHostErr = validateHost(MMS.AUTH, MMS.ENABLED, 'MMS');
    if (mmsHostErr) errs.MMS_HOST = mmsHostErr;
    const vmsHostErr = validateHost(VMS.AUTH, VMS.ENABLED, 'VMS');
    if (vmsHostErr) errs.VMS_HOST = vmsHostErr;
    const clientsHostErr = validateHost(
      CLIENTS.AUTH,
      CLIENTS.ENABLED,
      'CLIENTS',
    );
    if (clientsHostErr) errs.CLIENTS_HOST = clientsHostErr;

    const alertsMmsErr = validateHost(
      ALERTS.MMS.AUTH,
      ALERTS.MMS.ENABLED,
      'Alerts MMS',
    );
    if (alertsMmsErr) errs.ALERTS_MMS_HOST = alertsMmsErr;
    const alertsVmsErr = validateHost(
      ALERTS.VMS.AUTH,
      ALERTS.VMS.ENABLED,
      'Alerts VMS',
    );
    if (alertsVmsErr) errs.ALERTS_VMS_HOST = alertsVmsErr;
    const alertsClientsErr = validateHost(
      ALERTS.CLIENTS.AUTH,
      ALERTS.CLIENTS.ENABLED,
      'Alerts CLIENTS',
    );
    if (alertsClientsErr) errs.ALERTS_CLIENTS_HOST = alertsClientsErr;
    const alertsGatewayErr = validateHost(
      ALERTS.GATEWAY.AUTH,
      ALERTS.GATEWAY.ENABLED,
      'Alerts GATEWAY',
    );
    if (alertsGatewayErr) errs.ALERTS_GATEWAY_HOST = alertsGatewayErr;

    const scMmsErr = validateHost(
      SMART_COOLERS.MMS.AUTH,
      SMART_COOLERS.MMS.ENABLED,
      'SmartCoolers MMS',
    );
    if (scMmsErr) errs.SC_MMS_HOST = scMmsErr;
    const scVmsErr = validateHost(
      SMART_COOLERS.VMS.AUTH,
      SMART_COOLERS.VMS.ENABLED,
      'SmartCoolers VMS',
    );
    if (scVmsErr) errs.SC_VMS_HOST = scVmsErr;
    const scClientsErr = validateHost(
      SMART_COOLERS.CLIENTS.AUTH,
      SMART_COOLERS.CLIENTS.ENABLED,
      'SmartCoolers CLIENTS',
    );
    if (scClientsErr) errs.SC_CLIENTS_HOST = scClientsErr;
    const scGatewayErr = validateHost(
      SMART_COOLERS.GATEWAY.AUTH,
      SMART_COOLERS.GATEWAY.ENABLED,
      'SmartCoolers GATEWAY',
    );
    if (scGatewayErr) errs.SC_GATEWAY_HOST = scGatewayErr;

    return errs;
  }, [configData]);
  const hasConfigErrors = Object.keys(configErrors).length > 0;

  // For edit mode: button enabled if any field is dirty and no errors
  // For create mode: button enabled if all required fields are filled and dirty
  const parsedOriginalConfigs = React.useMemo(() => {
    const raw = providerData?.configs;
    if (!raw) return null;
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return raw;
    }
  }, [providerData?.configs]);
  const isLocalStateDirty =
    isEditMode &&
    (vdiVersion !== (providerData?.vdiVersion || VDI_VERSION.v2) ||
      syncVdi !== (providerData?.syncVdi ?? true));
  const allMandatoryFieldsValid = isEditMode
    ? (Object.keys(dirtyFields).length > 0 ||
      isLocalStateDirty ||
      JSON.stringify(configData) !== JSON.stringify(parsedOriginalConfigs)) &&
    _.isEmpty(errors) &&
    isServerValid &&
    isProfileValid &&
    isNameValid &&
    !hasConfigErrors
    : !!dirtyFields?.profile &&
    !!dirtyFields?.name &&
    !!isServerValid &&
    _.isEmpty(errors) &&
    !hasConfigErrors;

  const isSaveButtonEnabled = isSubmitting || !allMandatoryFieldsValid;

  // Helper function to validate server field
  const validateServerField = () => {
    setServerTouched(true);
    const valueToSet =
      server === PROVIDER_CONSTANT_PROPS.VALIDATION.SERVER_SELECT_PLACEHOLDER
        ? ''
        : server;
    setValue('server', valueToSet, {
      shouldValidate: true,
      shouldTouch: true,
      shouldDirty: true,
    });
    trigger('server');
  };

  const onSubmit: SubmitHandler<ICreateProviderFormInputs> = async (
    formData,
  ) => {
    const { name, partnerId: newPartnerId, server, profile } = formData;

    // Additional validation check for server
    if (
      !server ||
      server === '' ||
      server === PROVIDER_CONSTANT_PROPS.VALIDATION.SERVER_SELECT_PLACEHOLDER
    ) {
      setValue('server', '', {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
      trigger('server');
      return;
    }

    // Block submission if config validation errors exist
    if (hasConfigErrors) {
      return;
    }

    // If in edit mode and partnerId changed, show confirmation modal
    if (isEditMode && providerData?.partnerId !== newPartnerId) {
      setPendingUpdateData({
        ...formData,
        syncVdi,
        vdiVersion,
        configs: configData,
        providerId: providerData.providerId,
      });
      setIsConfirm(true);
      return;
    }

    setIsLoading(true);
    try {
      const configsObject = configData;
      const payload = {
        name,
        server,
        profile,
        syncVdi,
        vdiVersion,
        ...(!_.isEmpty(newPartnerId) ? { partnerId: newPartnerId } : {}),
        configs: configsObject,
        ...(isEditMode ? { providerId: providerData.providerId } : {}),
      };
      const upperCasedName = name?.toUpperCase();
      const upperCasedProfile = profile?.toUpperCase();
      const trimmedPayload = trimSpacesInPayload({
        name: upperCasedName,
        ...(!_.isEmpty(newPartnerId) ? { partnerId: newPartnerId } : {}),
        profile: upperCasedProfile,
      });
      const formattedPayload = { ...payload, ...trimmedPayload };

      // Dispatch create or update action based on mode
      if (isEditMode) {
        setUpdateInitiated(true);
        dispatch(updateProvider(formattedPayload));
      } else {
        dispatch(clearCreatedProviderData()); // clear stale error before new request
        dispatch(createProvider(formattedPayload));
      }
      setCreatedRecord({ name, partnerId: newPartnerId || '' });
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  // Handler for confirming the modal
  const onConformationClick = () => {
    if (!pendingUpdateData) return;
    if (
      confirmInput.trim().toLowerCase() !==
      PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
        .CONFIRM_KEYWORD
    ) {
      toast?.pushError(
        PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.UPDATE.CONFIRMATION_ERROR,
        USER_CONSTANT_PROPS?.GENERIC?.TOAST_TIME,
      );
      return;
    }
    setIsConfirm(false);
    setConfirmInput('');
    setIsLoading(true);
    try {
      const {
        name,
        partnerId: newPartnerId,
        server,
        profile,
        syncVdi,
        vdiVersion,
        configs,
        providerId,
      } = pendingUpdateData;
      const upperCasedName = name?.toUpperCase();
      const upperCasedProfile = profile?.toUpperCase();
      const trimmedPayload = trimSpacesInPayload({
        name: upperCasedName,
        ...(!_.isEmpty(newPartnerId) ? { partnerId: newPartnerId } : {}),
        profile: upperCasedProfile,
      });
      const formattedPayload = {
        name,
        server,
        profile,
        syncVdi,
        vdiVersion,
        ...(!_.isEmpty(newPartnerId) ? { partnerId: newPartnerId } : {}),
        configs,
        providerId,
        ...trimmedPayload,
      };
      setUpdateInitiated(true);
      dispatch(updateProvider(formattedPayload));
      setCreatedRecord({ name, partnerId: newPartnerId || '' });
    } catch (error: any) {
      setIsLoading(false);
    }
    setPendingUpdateData(null);
  };

  const onCloseConfirmation = () => {
    setIsConfirm(false);
    setConfirmInput('');
    setPendingUpdateData(null);
  };

  // Cleanup nav timeout on unmount
  useEffect(() => {
    return () => {
      if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
    };
  }, []);

  // Handle success response for both create and update
  useEffect(() => {
    const isCreateSuccess = createdProvider?.success && !error && !isEditMode;
    const isUpdateSuccess = isEditMode && updateInitiated && updateSuccess;

    // Stop loader on API error
    if (error && loading) {
      setIsLoading(false);
      setUpdateInitiated(false);
      return;
    }

    if (isCreateSuccess || isUpdateSuccess) {
      setIsLoading(false);

      if (isCreateSuccess) {
        reset({
          partnerId: '',
          name: '',
          server: '',
          profile: '',
          configs: '',
        });
        setSelectedServerObj(null);
        setConfigData(defaultConfigData);
        dispatch(clearCreatedProviderData());
      }

      if (isUpdateSuccess) {
        setUpdateInitiated(false);
        dispatch(clearUpdatedProviderData());
      }

      dispatch(setShouldRefetchProviders(true));

      // Guard: only navigate once — Redux dispatches above re-trigger this effect
      if (!hasNavigatedRef.current) {
        hasNavigatedRef.current = true;
        navTimeoutRef.current = setTimeout(() => {
          goBack();
        }, 1000);
      }
    }
  }, [
    createdProvider?.success,
    updateSuccess,
    updateInitiated,
    loading,
    isEditMode,
    error,
    dispatch,
    reset,
    goBack,
  ]);

  return (
    <>
      {/* Main Form Card - Full Width */}
      <PageCard testId={tid(TID.CRUD_PAGE)}>
        <PageToolbar
          icon={<LuBuilding2 className="w-5 h-5" />}
          title={
            isEditMode
              ? PROVIDER_CONSTANT_PROPS.EDIT_PROVIDER
              : PROVIDER_CONSTANT_PROPS.CREATE_PROVIDER
          }
          subtitle={
            isEditMode
              ? PROVIDER_CONSTANT_PROPS.PAGE.EDIT_SUBTITLE
              : PROVIDER_CONSTANT_PROPS.PAGE.CREATE_SUBTITLE
          }
          actionButtons={
            <AppButton
              data-testid={tid(TID.CRUD_CLOSE_BTN)}
              variant="neutral"
              shape="square"
              size="sm"
              type="button"
              onClick={goBack}
              aria-label="Close"
              className="hover:rotate-90 transition-transform duration-200"
              icon={<XMarkIcon className="w-4 h-4" />}
            />
          }
        />

        <div
          className={`px-4 py-4 sm:px-6 sm:py-6 md:px-10 lg:px-24 ${APP_BG.base} transition-colors duration-300`}
        >
          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5 sm:space-y-6"
          >
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:gap-8">
              {/* LEFT SIDE - Form Fields */}
              <div className="min-w-0 space-y-4">
                <SectionCard
                  icon={<LuFileText className="w-4 h-4" />}
                  title={PROVIDER_CONSTANT_PROPS.SECTIONS.PROVIDER_INFO_TITLE}
                  subtitle={
                    PROVIDER_CONSTANT_PROPS.SECTIONS.PROVIDER_INFO_SUBTITLE
                  }
                  bodyClassName="p-4"
                >
                  <div className="flex flex-col gap-4">
                    {/* VMS Server */}
                    <ConfigDropdown
                      dataTestId={tid(TID.CRUD_SERVER_SELECT)}
                      label={PROVIDER_CONSTANT_PROPS.FIELDS.VMS_SERVER_LABEL}
                      value={server}
                      options={SERVER_OPTIONS.map((opt) => ({
                        label: opt.label,
                        value: opt.value,
                      }))}
                      placeholder={
                        PROVIDER_CONSTANT_PROPS.FIELDS.VMS_SERVER_PLACEHOLDER
                      }
                      isRequired
                      error={errors?.server?.message}
                      onBlur={validateServerField}
                      icon={LuServer}
                      onChange={(val) => {
                        setServer(val);
                        setServerTouched(true);
                        setValue('server', val, {
                          shouldValidate: true,
                          shouldTouch: true,
                          shouldDirty: true,
                        });
                        setTimeout(() => trigger('server'), 100);
                      }}
                    />

                    {/* Profile Name */}
                    <ConfigInput
                      dataTestId={tid(TID.CRUD_PROFILE_INPUT)}
                      label={PROVIDER_CONSTANT_PROPS.FIELDS.PROFILE_NAME_LABEL}
                      required
                      placeholder={
                        PROVIDER_CONSTANT_PROPS.FIELDS.PROFILE_NAME_PLACEHOLDER
                      }
                      iconColor="text-primary"
                      inputClassName="uppercase"
                      value={watch ? watch('profile') || '' : ''}
                      error={errors?.profile?.message as string}
                      onChange={(e) => {
                        const val = e.target.value;
                        setValue('profile', val, {
                          shouldValidate: true,
                          shouldTouch: true,
                          shouldDirty: true,
                        });
                        setProfile(val);
                        trigger('profile');
                      }}
                      icon={LuUser}
                    />

                    {/* Provider Name */}
                    <ConfigInput
                      dataTestId={tid(TID.CRUD_NAME_INPUT)}
                      label={PROVIDER_CONSTANT_PROPS.FIELDS.PROVIDER_NAME_LABEL}
                      required
                      placeholder={
                        PROVIDER_CONSTANT_PROPS.FIELDS.PROVIDER_NAME_PLACEHOLDER
                      }
                      iconColor="text-primary"
                      inputClassName="uppercase"
                      value={watch ? watch('name') || '' : ''}
                      error={errors?.name?.message as string}
                      onChange={(e) => {
                        const val = e.target.value;
                        setValue('name', val, {
                          shouldValidate: true,
                          shouldTouch: true,
                          shouldDirty: true,
                        });
                        setName(val);
                        trigger('name');
                      }}
                      icon={LuBuilding2}
                    />

                    {/* Partner ID */}
                    <ConfigInput
                      dataTestId={tid(TID.CRUD_PARTNER_ID_INPUT)}
                      label={PROVIDER_CONSTANT_PROPS.FIELDS.PARTNER_ID_LABEL}
                      placeholder={
                        PROVIDER_CONSTANT_PROPS.FIELDS.PARTNER_ID_PLACEHOLDER
                      }
                      iconColor="text-primary"
                      value={watch ? watch('partnerId') || '' : ''}
                      error={errors?.partnerId?.message as string}
                      onChange={(e) => {
                        const val = e.target.value;
                        setValue('partnerId', val, {
                          shouldValidate: true,
                          shouldTouch: true,
                          shouldDirty: true,
                        });
                        setPartnerId(val);
                        trigger('partnerId');
                      }}
                      icon={LuHash}
                    />

                    {/* VDI Version */}
                    <div>
                      <label className="flex items-center gap-2 mb-2">
                        <svg
                          className="w-3 h-3 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                        <FieldLabel>
                          {PROVIDER_CONSTANT_PROPS.FIELDS.VDI_VERSION_LABEL}
                        </FieldLabel>
                      </label>
                      <div className="flex flex-row gap-3">
                        {/* V1 */}
                        <button
                          data-testid={tid(TID.CRUD_VDI_V1_BTN)}
                          type="button"
                          onClick={() => setVdiVersion(VDI_VERSION.v1)}
                          className={`flex-1 cursor-pointer rounded-xl border px-4 py-3 transition-all duration-200 text-left ${vdiVersion === VDI_VERSION.v1
                            ? 'border-primary/40 bg-primary/5 dark:bg-primary/10 shadow-sm'
                            : 'border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:border-primary/30 dark:hover:border-white/[0.14]'
                            }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <AppRadio
                              checked={vdiVersion === VDI_VERSION.v1}
                              onChange={() => setVdiVersion(VDI_VERSION.v1)}
                              size="sm"
                              color="primary"
                              variant={
                                vdiVersion === VDI_VERSION.v1
                                  ? 'filled'
                                  : 'outlined'
                              }
                              className="pointer-events-none flex-shrink-0"
                            />
                            <Text
                              as="span"
                              size="xl"
                              weight="bold"
                              color={
                                vdiVersion === VDI_VERSION.v1
                                  ? 'primary'
                                  : 'default'
                              }
                            >
                              {PROVIDER_CONSTANT_PROPS.FIELDS.V1_LABEL}
                            </Text>
                          </div>
                        </button>

                        {/* V2 */}
                        <button
                          data-testid={tid(TID.CRUD_VDI_V2_BTN)}
                          type="button"
                          onClick={() => setVdiVersion(VDI_VERSION.v2)}
                          className={`flex-1 cursor-pointer rounded-xl border px-4 py-3 transition-all duration-200 text-left ${vdiVersion === VDI_VERSION.v2
                            ? 'border-primary/40 bg-primary/5 dark:bg-primary/10 shadow-sm'
                            : 'border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:border-primary/30 dark:hover:border-white/[0.14]'
                            }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <AppRadio
                              checked={vdiVersion === VDI_VERSION.v2}
                              onChange={() => setVdiVersion(VDI_VERSION.v2)}
                              size="sm"
                              color="primary"
                              variant={
                                vdiVersion === VDI_VERSION.v2
                                  ? 'filled'
                                  : 'outlined'
                              }
                              className="pointer-events-none flex-shrink-0"
                            />
                            <Text
                              as="span"
                              size="xl"
                              weight="bold"
                              color={
                                vdiVersion === VDI_VERSION.v2
                                  ? 'primary'
                                  : 'default'
                              }
                            >
                              {PROVIDER_CONSTANT_PROPS.FIELDS.V2_LABEL}
                            </Text>
                          </div>
                        </button>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

                    {/* VDI Sync */}
                    <div
                      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg`}
                    >
                      <div className="flex min-w-0 items-center gap-3 flex-1">
                        <IconBadge
                          icon={<LuRefreshCw />}
                          size="sm"
                          variant={syncVdi ? 'light' : 'flat'}
                          color={syncVdi ? APP_HEX.successStrong : undefined}
                        />
                        <div>
                          <FieldLabel as="p" size="lg">
                            {PROVIDER_CONSTANT_PROPS.FIELDS.VDI_SYNC_LABEL}
                          </FieldLabel>
                          <Text
                            as="p"
                            size="base"
                            weight="medium"
                            color={syncVdi ? 'success' : 'muted'}
                          >
                            {syncVdi
                              ? PROVIDER_CONSTANT_PROPS.FIELDS.VDI_SYNC_ENABLED
                              : PROVIDER_CONSTANT_PROPS.FIELDS
                                .VDI_SYNC_DISABLED}
                          </Text>
                        </div>
                      </div>
                      <AppToggle
                        data-testid={tid(TID.CRUD_SYNC_TOGGLE)}
                        checked={syncVdi}
                        onChange={() => {
                          // Only allow enabling (false→true); once true it's locked
                          if (!syncVdi) setSyncVdi(true);
                        }}
                        disabled={syncVdi}
                        color="green"
                      />
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* RIGHT SIDE - Configuration */}
              <div className="min-w-0 space-y-4">
                <SectionCard
                  icon={<LuSettings2 className="w-4 h-4" />}
                  title={PROVIDER_CONSTANT_PROPS.SECTIONS.CONFIG_TITLE}
                  subtitle={PROVIDER_CONSTANT_PROPS.SECTIONS.CONFIG_SUBTITLE}
                  bodyClassName="p-3"
                >
                  <ProviderConfiguration
                    configData={configData}
                    setConfigData={setConfigData}
                    configErrors={configErrors}
                  />
                </SectionCard>
              </div>
            </div>

            {/* Action Buttons - Compact Design */}
            <div className="mt-6 flex flex-col gap-4 border-t border-gray-200 pt-6 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400 sm:items-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <Caption size="base" color="muted">
                  {PROVIDER_CONSTANT_PROPS.PAGE.REQUIRED_FIELDS_HINT}
                </Caption>
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                {!isEditMode && (
                  <AppButton
                    data-testid={tid(TID.CRUD_RESET_BTN)}
                    type="button"
                    variant="primary"
                    tint
                    size="xl"
                    className="w-full sm:w-auto"
                    icon={<LuRotateCcw className="w-3.5 h-3.5" />}
                    onClick={() => {
                      reset({
                        partnerId: '',
                        name: '',
                        server: '',
                        profile: '',
                        configs: '',
                      });
                      setValue('server', '', {
                        shouldValidate: false,
                        shouldDirty: false,
                      });
                      setSelectedServerObj(null);
                      setServer('');
                      setName('');
                      setProfile('');
                      setPartnerId('');
                      setVdiVersion(VDI_VERSION.v2);
                      setSyncVdi(true);
                      setConfigData(defaultConfigData);
                      setServerTouched(false);
                    }}
                  >
                    {PROVIDER_CONSTANT_PROPS.PAGE.RESET}
                  </AppButton>
                )}
                <AppButton
                  data-testid={tid(TID.CRUD_CANCEL_BTN)}
                  type="button"
                  variant="neutral"
                  size="xl"
                  className="w-full sm:w-auto"
                  icon={<XMarkIcon className="w-4 h-4" />}
                  onClick={goBack}
                >
                  {PROVIDER_CONSTANT_PROPS.PAGE.CANCEL}
                </AppButton>
                <AppButton
                  type="submit"
                  variant="primary"
                  size="xl"
                  className="w-full sm:w-auto"
                  loading={loading || isSubmitting}
                  disabled={!loading && !isSubmitting && isSaveButtonEnabled}
                  data-testid={tid(TID.CRUD_SAVE_BTN)}
                  icon={
                    isEditMode ? (
                      <IoCheckmarkCircle className="w-4 h-4" />
                    ) : (
                      <IoMdAddCircle className="w-4 h-4" />
                    )
                  }
                  onClick={() => {
                    if (
                      !server ||
                      server === '' ||
                      server ===
                      PROVIDER_CONSTANT_PROPS.VALIDATION
                        .SERVER_SELECT_PLACEHOLDER
                    ) {
                      validateServerField();
                    }
                  }}
                >
                  {isEditMode
                    ? PROVIDER_CONSTANT_PROPS.PAGE.UPDATE_PROVIDER
                    : PROVIDER_CONSTANT_PROPS.CREATE_PROVIDER}
                </AppButton>
              </div>
            </div>
          </form>

          {/* Confirmation Modal for Partner ID change */}
          <Modal
            open={isEditMode && isConfirm && !!pendingUpdateData}
            onClose={onCloseConfirmation}
            variant="warning"
            size="xl"
          >
            <Modal.Header
              title={
                PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                  .TITLE
              }
              subtitle={
                PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                  .SUBTITLE
              }
              icon={
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              }
            />
            <Modal.Body>
              <Text as="p" size="xl" color="muted" className="mb-4">
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .BODY_PREFIX
                }{' '}
                <Text as="span" size="xl" weight="semibold" color="default">
                  {providerData?.partnerId ||
                    PROVIDER_CONSTANT_PROPS.VALIDATION.NA_FALLBACK}
                </Text>{' '}
                to{' '}
                <Text as="span" size="xl" weight="semibold" color="default">
                  {pendingUpdateData?.partnerId}
                </Text>{' '}
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .BODY_SUFFIX
                }
              </Text>
              <Label className="block mb-1.5">
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .CONFIRM_LABEL
                }
              </Label>
              <input
                className={`focus:outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-primary/40 w-full h-10 border border-gray-200 dark:border-gray-700 rounded-xl py-2 px-4 ${APP_BG.base} dark:text-white placeholder-gray-300 dark:placeholder-gray-600 text-sm transition-colors`}
                placeholder={
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .INPUT_PLACEHOLDER
                }
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                autoComplete="off"
              />
              {confirmInput &&
                confirmInput.trim().toLowerCase() !==
                PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                  .CONFIRM_KEYWORD && (
                  <Text as="p" size="base" color="secondary" className="mt-1.5">
                    {
                      PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS
                        .PARTNER_ID_CHANGE.VALIDATION_MSG_PREFIX
                    }{' '}
                    <code
                      className={`font-mono ${APP_BG.code} border border-gray-200 dark:border-gray-700 px-1 py-0.5 rounded text-gray-600 dark:text-gray-300`}
                    >
                      {
                        PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS
                          .PARTNER_ID_CHANGE.CONFIRM_KEYWORD
                      }
                    </code>{' '}
                    {
                      PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS
                        .PARTNER_ID_CHANGE.VALIDATION_MSG_SUFFIX
                    }
                  </Text>
                )}
              <Text as="p" size="md" color="subtle" className="mt-1.5">
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .HINT_MSG_PREFIX
                }{' '}
                <code
                  className={`font-mono ${APP_BG.code} border border-gray-200 dark:border-gray-700 px-1 py-0.5 rounded text-gray-600 dark:text-gray-300`}
                >
                  {
                    PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS
                      .PARTNER_ID_CHANGE.CONFIRM_KEYWORD
                  }
                </code>{' '}
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .HINT_MSG_SUFFIX
                }
              </Text>
            </Modal.Body>
            <Modal.Footer>
              <Modal.CancelButton
                testId={TID.CRUD_CONFIRM_MODAL_CANCEL_BTN}
                onClick={onCloseConfirmation}
              >
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .CANCEL
                }
              </Modal.CancelButton>
              <Modal.ConfirmButton
                testId={tid(TID.CRUD_CONFIRM_MODAL_UPDATE_BTN)}
                onClick={onConformationClick}
                icon={<LuRefreshCw className="w-3.5 h-3.5" />}
                disabled={
                  confirmInput.trim().toLowerCase() !==
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .CONFIRM_KEYWORD
                }
              >
                {
                  PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.PARTNER_ID_CHANGE
                    .UPDATE_BUTTON
                }
              </Modal.ConfirmButton>
            </Modal.Footer>
          </Modal>
        </div>
      </PageCard>
    </>
  );
};

export default CreateEditProvider;
